/**
 * Currency Context
 * Provides app-wide currency management and formatting
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './FirebaseAuthContext';
import { detectCountry, getCurrencyForCountry, formatCurrency, getCurrencySymbol, DEFAULT_CURRENCY, DEFAULT_COUNTRY } from '@/lib/currency';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const { user, profile } = useAuth();
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);

  // Load currency from user profile or detect from timezone
  useEffect(() => {
    const loadCurrency = () => {
      // If user has a profile with currency, use it
      if (profile?.currency) {
        setCurrency(profile.currency);
        if (profile.country) {
          setCountry(profile.country);
        }
        return;
      }

      // If user is logged in but no currency set, detect from timezone
      if (user && !profile?.currency) {
        try {
          const detectedCountry = detectCountry();
          const detectedCurrency = getCurrencyForCountry(detectedCountry);
          setCountry(detectedCountry);
          setCurrency(detectedCurrency);
          console.log('🌍 Detected currency for user:', detectedCurrency, 'from country:', detectedCountry);
        } catch (error) {
          console.error('Error detecting currency:', error);
          setCountry(DEFAULT_COUNTRY);
          setCurrency(DEFAULT_CURRENCY);
        }
      } else if (!user) {
        // For anonymous users, detect from timezone
        try {
          const detectedCountry = detectCountry();
          const detectedCurrency = getCurrencyForCountry(detectedCountry);
          setCountry(detectedCountry);
          setCurrency(detectedCurrency);
        } catch (error) {
          console.error('Error detecting currency:', error);
          setCountry(DEFAULT_COUNTRY);
          setCurrency(DEFAULT_CURRENCY);
        }
      }
    };

    loadCurrency();
  }, [user, profile]);

  // Update currency when profile changes
  useEffect(() => {
    if (profile?.currency) {
      setCurrency(profile.currency);
    }
    if (profile?.country) {
      setCountry(profile.country);
    }
  }, [profile]);

  // Format currency helper
  const format = useCallback((amount) => {
    return formatCurrency(amount, currency);
  }, [currency]);

  // Get currency symbol
  const symbol = useCallback(() => {
    return getCurrencySymbol(currency);
  }, [currency]);

  // Update currency (for user settings)
  const updateCurrency = useCallback((newCurrency, newCountry = null) => {
    setCurrency(newCurrency);
    if (newCountry) {
      setCountry(newCountry);
    }
  }, []);

  const value = {
    currency,
    country,
    format,
    symbol,
    updateCurrency,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

